import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight, ShieldCheck, Target, Flame, AlertTriangle,
  Dumbbell, Brain, Users, Clock, TrendingDown, PlayCircle,
} from "lucide-react";

/**
 * Arruolamento — Pubblicità Accademia LSSD.
 * Pagina intermedia che precede il form di candidatura.
 * Tono: onesto, duro, senza fronzoli — "solo il 40% supera, chi ha coraggio dura".
 */

const HERO_IMG = "/media/hero-explorer-still.jpg";
const FORMATION_IMG = "/media/academy-formation.png";
const CEREMONY_IMG = "/media/academy-ceremony.jpg";
const PATROL_IMG = "/media/academy-patrol.png";

// Path opzionale: se esiste `/media/reel-academy.mp4` verrà usato al posto dello slideshow
const REEL_VIDEO_SRC = "/media/reel-academy.mp4";

const REEL_SLIDES = [
  {
    img: FORMATION_IMG,
    kicker: "Formazione · Onore",
    line: "Il primo giorno inizia con un giuramento.",
    from: { scale: 1.05, x: 0, y: 0 },
    to:   { scale: 1.18, x: -2, y: -3 },
  },
  {
    img: CEREMONY_IMG,
    kicker: "Cerimonia · Contea",
    line: "Sotto lo sguardo di chi ci ha voluto.",
    from: { scale: 1.10, x: 2, y: 0 },
    to:   { scale: 1.22, x: -1, y: 2 },
  },
  {
    img: PATROL_IMG,
    kicker: "Pattuglia · Servizio",
    line: "H24. Ogni giorno, ogni turno.",
    from: { scale: 1.06, x: 0, y: 2 },
    to:   { scale: 1.20, x: 2, y: -2 },
  },
];

const SLIDE_MS = 5500;

function TrainingReel() {
  const [idx, setIdx] = useState(0);
  const [hasVideo, setHasVideo] = useState(false);

  // Prova a caricare l'MP4 opzionale. Se non esiste (dev server risponde HTML), usa lo slideshow Ken Burns.
  useEffect(() => {
    let cancelled = false;
    fetch(REEL_VIDEO_SRC, { method: "HEAD" })
      .then((r) => {
        const ct = r.headers.get("content-type") || "";
        if (!cancelled && r.ok && ct.startsWith("video/")) setHasVideo(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Auto-advance slideshow
  useEffect(() => {
    if (hasVideo) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % REEL_SLIDES.length), SLIDE_MS);
    return () => clearInterval(t);
  }, [hasVideo]);

  const active = REEL_SLIDES[idx];

  return (
    <section
      className="bg-[var(--lssd-olive-dark)] border-b border-[var(--lssd-khaki)]/20 py-16 sm:py-20"
      data-testid="arruolamento-reel"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="text-center mb-10 max-w-3xl mx-auto">
          <div className="kicker no-line mb-3 justify-center">Reel Addestramento</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-[var(--lssd-cream)] tracking-tight leading-tight mb-3">
            L&apos;Accademia <span className="text-[var(--lssd-khaki)]">in movimento.</span>
          </h2>
          <p className="text-[var(--lssd-cream)]/65 text-[15px] leading-relaxed">
            Alcuni istanti che raccontano meglio di mille parole cosa significa indossare la stella.
          </p>
        </div>

        <div className="relative rounded-3xl overflow-hidden border border-[var(--lssd-khaki)]/25 bg-black shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]">
          {/* linea oro superiore */}
          <div className="absolute top-0 inset-x-8 h-[2px] bg-gradient-to-r from-transparent via-[var(--lssd-khaki)] to-transparent z-30" />

          {/* Cornice cinematica 21:9 */}
          <div className="relative w-full" style={{ aspectRatio: "21 / 9" }}>

            {hasVideo ? (
              <video
                src={REEL_VIDEO_SRC}
                autoPlay
                loop
                muted
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="reel-video"
              />
            ) : (
              REEL_SLIDES.map((slide, i) => (
                <img
                  key={slide.img}
                  src={slide.img}
                  alt={slide.kicker}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === idx ? "opacity-100 kenburns-active" : "opacity-0"}`}
                  style={{
                    "--kb-from-scale": slide.from.scale,
                    "--kb-to-scale": slide.to.scale,
                    "--kb-from-x": `${slide.from.x}%`,
                    "--kb-to-x": `${slide.to.x}%`,
                    "--kb-from-y": `${slide.from.y}%`,
                    "--kb-to-y": `${slide.to.y}%`,
                  }}
                  data-testid={`reel-slide-${i}`}
                />
              ))
            )}

            {/* Vignetta olive per uniformità visiva */}
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--lssd-olive-deep)]/85 via-[var(--lssd-olive-deep)]/20 to-[var(--lssd-olive-deep)]/50 pointer-events-none" />

            {/* Overlay text bottom-left */}
            <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10 z-20 pointer-events-none">
              <div className="flex items-center gap-2 mb-2">
                <PlayCircle className="h-4 w-4 text-[var(--lssd-khaki)]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--lssd-khaki)]">
                  {hasVideo ? "Reel · Live" : active.kicker}
                </span>
              </div>
              <div className="font-display font-black text-2xl sm:text-3xl lg:text-4xl text-[var(--lssd-cream)] leading-tight max-w-2xl">
                {hasVideo ? "In addestramento, in servizio, in onore." : active.line}
              </div>
            </div>

            {/* Progress bar in basso */}
            {!hasVideo && (
              <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[var(--lssd-olive-deep)]/50 z-20">
                <div
                  key={idx}
                  className="h-full bg-[var(--lssd-khaki)] reel-progress"
                  style={{ animationDuration: `${SLIDE_MS}ms` }}
                />
              </div>
            )}

            {/* Contatore slide top-right */}
            {!hasVideo && (
              <div className="absolute top-5 right-5 z-20 flex items-center gap-1.5 rounded-full bg-black/40 backdrop-blur-md border border-[var(--lssd-khaki)]/35 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--lssd-khaki)]">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--lssd-khaki)] opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[var(--lssd-khaki)]" />
                </span>
                Reel · {String(idx + 1).padStart(2, "0")} / {String(REEL_SLIDES.length).padStart(2, "0")}
              </div>
            )}
          </div>
        </div>

        {/* Thumbnails clickable */}
        {!hasVideo && (
          <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
            {REEL_SLIDES.map((s, i) => (
              <button
                key={s.img}
                onClick={() => setIdx(i)}
                data-testid={`reel-thumb-${i}`}
                className={`group relative aspect-video rounded-lg overflow-hidden border transition-all ${i === idx ? "border-[var(--lssd-khaki)] ring-2 ring-[var(--lssd-khaki)]/40" : "border-[var(--lssd-khaki)]/20 hover:border-[var(--lssd-khaki)]/60 opacity-70 hover:opacity-100"}`}
                aria-label={`Vai alla slide ${i + 1}`}
              >
                <img src={s.img} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--lssd-olive-deep)]/80 to-transparent" />
                <div className="absolute bottom-1.5 left-2 font-mono text-[9px] uppercase tracking-[0.18em] font-bold text-[var(--lssd-cream)]">
                  {String(i + 1).padStart(2, "0")}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

const REALITIES = [
  { Icon: Dumbbell, title: "Preparazione Fisica Estrema",    body: "Test di forza, resistenza, corsa e ostacoli superati al 100%. Chi arriva stanco ai primi giorni non arriva alla fine." },
  { Icon: Brain,    title: "Pressione Psicologica Costante", body: "Simulazioni di crisi, gestione dello stress, decision-making sotto fuoco. La divisa pesa più del kevlar." },
  { Icon: Target,   title: "Standard Operativi Non Negoziabili", body: "Precisione al tiro, procedure radio, uso della forza — sbagli anche una sola valutazione e l'Accademia finisce lì." },
  { Icon: Users,    title: "Cameratismo o Solitudine",         body: "Chi non fa squadra viene lasciato indietro. Il LSSD non è per chi cerca gloria personale — è per chi copre il compagno." },
];

export default function Arruolamento() {
  // Numeri fissi coerenti col 40% di superamento — dati "vetrina" del bando
  const received = 220;
  const graduated = 88;
  const dropped = received - graduated;
  const gradPct = (graduated / received) * 100;
  const dropPct = 100 - gradPct;
  const currentYear = new Date().getFullYear();

  const STATS = [
    {
      value: "40%",
      label: "Tasso di Superamento",
      Icon: TrendingDown,
      note: "Solo 4 su 10 candidati completano l'Accademia",
    },
    { value: "1",    label: "Settimana di Addestramento", Icon: Clock,  note: "Corso intensivo full-time — 7 giorni non-stop" },
    { value: "15",   label: "Ore Operative Richieste",    Icon: Target, note: "Tra teoria, tiro, guida e tattica sul campo" },
    { value: "24/7", label: "Disponibilità al Servizio",  Icon: Flame,  note: "Chi entra sa che la stella non dorme" },
  ];

  return (
    <div data-testid="arruolamento-page" className="bg-[var(--lssd-olive-deep)]">

      {/* ============ HERO — dichiarazione d'intenti ============ */}
      <section className="relative overflow-hidden border-b border-[var(--lssd-khaki)]/30">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <div className="absolute inset-0 bg-[var(--lssd-olive-deep)]/90" />
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--lssd-olive-deep)] via-[var(--lssd-olive-deep)]/60 to-[var(--lssd-olive-deep)]" />
        </div>
        <div className="h-[3px] w-full bg-gradient-to-r from-transparent via-[var(--lssd-khaki)] to-transparent absolute top-0" />

        <div className="relative max-w-5xl mx-auto px-6 sm:px-10 py-20 sm:py-28 text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <span className="h-px w-10 bg-[var(--lssd-khaki)]/60" />
            <span className="text-[var(--lssd-khaki)] text-xl leading-none">★</span>
            <span className="h-px w-10 bg-[var(--lssd-khaki)]/60" />
          </div>

          <div className="kicker no-line mb-4 justify-center">Accademia LSSD · Reclutamenti Aperti</div>

          <h1 className="font-display font-black text-[clamp(2.25rem,6vw,5.25rem)] text-[var(--lssd-cream)] leading-[0.95] tracking-tight mb-6">
            Chi entra <span className="text-[var(--lssd-khaki)]">esce diverso.</span><br />
            Se esce.
          </h1>

          <p className="text-lg sm:text-xl text-[var(--lssd-cream)]/80 max-w-3xl mx-auto leading-relaxed mb-10">
            L&apos;Accademia del Los Santos County Sheriff&apos;s Department non è un corso di formazione.
            È una selezione. <span className="text-[var(--lssd-khaki)] font-semibold">Solo il 40% supera</span> —
            solo chi ha davvero il coraggio dura.
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/reclutamenti" data-testid="arruolamento-primary-cta">
              <span className="btn-gold">Invia la tua candidatura <ArrowRight className="h-4 w-4" /></span>
            </Link>
            <a href="#realta" data-testid="arruolamento-secondary-cta">
              <span className="btn-inst-outline">
                <AlertTriangle className="h-4 w-4" /> Scopri cosa ti aspetta
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* ============ STATS — i numeri parlano chiaro ============ */}
      <section className="bg-[var(--lssd-olive-dark)] border-b border-[var(--lssd-khaki)]/20 py-16" data-testid="arruolamento-stats">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="text-center mb-12">
            <div className="kicker no-line mb-3 justify-center">I Numeri dell&apos;Accademia</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-[var(--lssd-cream)] tracking-tight">
              Nessun ritocco.<br />
              <span className="text-[var(--lssd-khaki)]">Solo dati reali.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STATS.map((s) => (
              <div
                key={s.label}
                data-testid={`stat-${s.label}`}
                className="relative rounded-2xl bg-[var(--lssd-olive)] border border-[var(--lssd-khaki)]/25 p-6 overflow-hidden"
              >
                <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-[var(--lssd-khaki)]/70 to-transparent" />
                {s.live && (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400 font-bold">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                    </span>
                    Live
                  </span>
                )}
                <div className="flex items-start justify-between mb-4">
                  <div className="font-display font-black text-4xl sm:text-5xl text-[var(--lssd-khaki)] leading-none tracking-tight">
                    {s.value}
                  </div>
                  <div className="h-10 w-10 rounded-full bg-[var(--lssd-khaki)]/18 border border-[var(--lssd-khaki)]/45 flex items-center justify-center shrink-0">
                    <s.Icon className="h-4 w-4 text-[var(--lssd-khaki)]" />
                  </div>
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--lssd-cream)] mb-2">
                  {s.label}
                </div>
                <p className="text-[13px] text-[var(--lssd-cream)]/60 leading-relaxed">{s.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ REEL ADDESTRAMENTO — slideshow cinematico Ken Burns ============ */}
      <TrainingReel />

      {/* ============ REALTÀ — cosa ti aspetta ============ */}
      <section id="realta" className="bg-[var(--lssd-olive-deep)] py-20" data-testid="arruolamento-realities">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <div className="kicker no-line mb-3 justify-center">Cosa Ti Aspetta</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-[var(--lssd-cream)] tracking-tight leading-tight mb-4">
              Prima di firmare, <span className="text-[var(--lssd-khaki)]">leggi tutto.</span>
            </h2>
            <p className="text-[var(--lssd-cream)]/70 leading-relaxed">
              L&apos;Accademia LSSD ti spinge oltre i limiti che credi di conoscere.
              Qui non esistono scorciatoie né trattamenti di favore.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {REALITIES.map((r, i) => (
              <div
                key={r.title}
                data-testid={`reality-${i}`}
                className="relative rounded-2xl bg-[var(--lssd-olive-dark)] border border-[var(--lssd-khaki)]/20 hover:border-[var(--lssd-khaki)]/60 p-7 transition-colors group overflow-hidden"
              >
                <div className="absolute left-0 top-8 bottom-8 w-[3px] bg-[var(--lssd-khaki)] rounded-r-full" />
                <div className="flex items-start gap-5">
                  <div className="h-12 w-12 rounded-full bg-[var(--lssd-khaki)]/18 border border-[var(--lssd-khaki)]/45 flex items-center justify-center shrink-0">
                    <r.Icon className="h-5 w-5 text-[var(--lssd-khaki)]" />
                  </div>
                  <div className="flex-1">
                    <div className="font-mono text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--lssd-khaki)] mb-2">
                      Requisito 0{i + 1}
                    </div>
                    <h3 className="font-display font-bold text-xl text-[var(--lssd-cream)] leading-tight mb-2">
                      {r.title}
                    </h3>
                    <p className="text-[14px] text-[var(--lssd-cream)]/70 leading-relaxed">
                      {r.body}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WARNING — chi non ce la fa ============ */}
      <section className="bg-[var(--lssd-olive-dark)] border-y border-[var(--lssd-khaki)]/25 py-20" data-testid="arruolamento-warning">
        <div className="max-w-5xl mx-auto px-6 sm:px-10">
          <div className="rounded-3xl bg-gradient-to-br from-[var(--lssd-olive-deep)] via-[var(--lssd-olive)] to-[var(--lssd-olive-dark)] border border-[var(--lssd-khaki)]/40 p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[var(--lssd-khaki)]/10 blur-3xl pointer-events-none" />
            <div className="absolute -left-16 -bottom-16 h-56 w-56 rounded-full bg-[var(--lssd-khaki)]/8 blur-3xl pointer-events-none" />
            <div className="relative flex flex-col sm:flex-row items-start gap-6">
              <div className="h-14 w-14 rounded-full bg-[var(--lssd-khaki)]/20 border border-[var(--lssd-khaki)]/50 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-6 w-6 text-[var(--lssd-khaki)]" />
              </div>
              <div className="flex-1">
                <div className="kicker mb-3">Avviso Ufficiale del Comando</div>
                <h3 className="font-display font-black text-2xl sm:text-3xl text-[var(--lssd-cream)] leading-tight mb-4">
                  Se cerchi <span className="text-[var(--lssd-khaki)]">una divisa comoda</span>,<br className="hidden sm:block" />
                  chiudi questa pagina.
                </h3>
                <p className="text-[var(--lssd-cream)]/80 text-[15px] leading-relaxed">
                  Il <span className="text-[var(--lssd-khaki)] font-semibold">60%</span> dei candidati non completa l&apos;Accademia.
                  Non per cattiveria degli istruttori — ma perché la stella dello Sceriffo{" "}
                  <span className="text-[var(--lssd-khaki)] font-semibold">non si regala a nessuno</span>.
                  Chi ha coraggio, integrità e voglia di servire — dura.
                  Gli altri escono nella prima settimana.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SOCIAL PROOF — contatore duro ============ */}
      <section className="bg-[var(--lssd-olive-deep)] py-16 sm:py-20" data-testid="arruolamento-counter">
        <div className="max-w-5xl mx-auto px-6 sm:px-10">
          <div className="text-center mb-10">
            <div className="kicker no-line mb-3 justify-center">Statistica di Bando Corrente</div>
            <h2 className="font-display font-black text-2xl sm:text-3xl text-[var(--lssd-cream)] tracking-tight">
              I numeri di <span className="text-[var(--lssd-khaki)]">quest&apos;anno</span>.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Domande ricevute */}
            <div className="relative rounded-2xl bg-[var(--lssd-olive-dark)] border border-[var(--lssd-khaki)]/25 p-8 overflow-hidden" data-testid="counter-received">
              <div className="absolute top-0 left-8 right-8 h-[2px] bg-gradient-to-r from-transparent via-[var(--lssd-khaki)] to-transparent" />
              <div className="flex items-center gap-3 mb-3">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--lssd-khaki)] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--lssd-khaki)]" />
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--lssd-khaki)]">
                  Anno {currentYear}
                </span>
              </div>
              <div className="font-display font-black text-6xl sm:text-7xl text-[var(--lssd-khaki)] leading-none tracking-tight mb-2" data-testid="counter-received-value">
                {received}
              </div>
              <div className="text-[13px] uppercase tracking-[0.22em] font-bold text-[var(--lssd-cream)] mb-2">
                Candidature Pervenute
              </div>
              <p className="text-[13px] text-[var(--lssd-cream)]/65 leading-relaxed">
                Il Comando ha ricevuto <span className="text-[var(--lssd-khaki)] font-semibold">{received} candidature</span> in questo bando.
                Ogni fascicolo viene letto personalmente dallo staff HR.
              </p>
            </div>

            {/* Completate */}
            <div className="relative rounded-2xl bg-[var(--lssd-olive-dark)] border border-red-500/30 p-8 overflow-hidden" data-testid="counter-completed">
              <div className="absolute top-0 left-8 right-8 h-[2px] bg-gradient-to-r from-transparent via-red-500/70 to-transparent" />
              <div className="flex items-center gap-3 mb-3">
                <span className="relative flex h-2 w-2">
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] font-bold text-red-400">
                  Realtà Cruda
                </span>
              </div>
              <div className="font-display font-black text-6xl sm:text-7xl text-red-400 leading-none tracking-tight mb-2" data-testid="counter-completed-value">
                {graduated}
              </div>
              <div className="text-[13px] uppercase tracking-[0.22em] font-bold text-[var(--lssd-cream)] mb-2">
                Hanno Finito l&apos;Accademia
              </div>
              <p className="text-[13px] text-[var(--lssd-cream)]/65 leading-relaxed">
                Solo <span className="text-red-400 font-semibold">{graduated} candidati</span> hanno superato l&apos;Accademia
                e indossano oggi la stella. Gli altri {dropped} sono usciti prima del giuramento.
              </p>
            </div>
          </div>

          {/* Progress bar visualizza la ratio */}
          <div className="mt-8 rounded-full bg-[var(--lssd-olive-dark)] border border-[var(--lssd-khaki)]/25 overflow-hidden h-3 relative">
            <div
              className="h-full bg-gradient-to-r from-red-500 via-[var(--lssd-khaki)] to-[var(--lssd-khaki-light)] transition-all duration-700"
              style={{ width: `${gradPct}%` }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em]">
            <span className="text-red-400 font-bold">{graduated} giurati · {gradPct.toFixed(1)}%</span>
            <span className="text-[var(--lssd-cream)]/50">{dropped} usciti · {dropPct.toFixed(1)}%</span>
          </div>
        </div>
      </section>

      {/* ============ CTA FINALE — ora decidi ============ */}
      <section className="bg-[var(--lssd-olive-deep)] py-20 relative overflow-hidden" data-testid="arruolamento-final-cta">
        <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[var(--lssd-khaki)] to-transparent" />
        <div className="max-w-4xl mx-auto px-6 sm:px-10 text-center">
          <div className="flex items-center justify-center mb-6">
            <ShieldCheck className="h-6 w-6 text-[var(--lssd-khaki)]" />
          </div>
          <h2 className="font-display font-black text-3xl sm:text-5xl text-[var(--lssd-cream)] leading-[1.05] tracking-tight mb-5">
            Ancora convinto?<br />
            <span className="text-[var(--lssd-khaki)]">Allora fatti avanti.</span>
          </h2>
          <p className="text-[var(--lssd-cream)]/75 max-w-2xl mx-auto leading-relaxed mb-8">
            La candidatura è il primo test. Compila ogni sezione con serietà —
            il Comando valuta ogni parola.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/reclutamenti" data-testid="arruolamento-final-cta-primary">
              <span className="btn-gold">Compila la Candidatura <ArrowRight className="h-4 w-4" /></span>
            </Link>
            <Link to="/candidatura" data-testid="arruolamento-final-cta-secondary">
              <span className="btn-inst-outline">Ho già inviato — Stato Bando</span>
            </Link>
          </div>

          <p className="mt-10 text-[11px] uppercase tracking-[0.24em] font-mono text-[var(--lssd-khaki)]/70">
            Honor · Integrity · Service · Since 1852
          </p>
        </div>
      </section>
    </div>
  );
}
